import React, { useState, useEffect } from "react";
import { getAPIURL, getVendorID, getVendorSettings } from "../../services/helper";
import http from "../../services/http";
import DataTable from "react-data-table-component";
import {
  Spinner,
  Container,
  Card,
  Input,
  Button,
  Row,
  Col,
  CardBody,
  CardTitle,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import {
  FaShoppingCart,
  FaDollarSign,
  FaReceipt,
  FaCashRegister,
  FaPercent,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { openView } from "../../ReduxToolkit/Reducers/viewReducer";
import classnames from "classnames";

const SalesReportByCategory = ({ backTo }) => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const dispatch = useDispatch();
  const [data, setData] = useState({ summary: {}, details: [] });
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0], // Last month
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchSalesReport();
  }, []);

  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ vendor_id: vendorId });
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);

      const { data } = await http.get(
        `${api_url}/reports/sales/categories?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching sales report:", error);
    }
    setLoading(false);
  };

  const printReport = () => {
    const vendor_email = getVendorSettings("email");
    const vendor_store_name = getVendorSettings("name");
    const vendor_address = getVendorSettings("vendor_contact_address");
    const vendor_phone = getVendorSettings("vendor_contact_phone");
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Sales Report by Categories</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h2 { margin: 0; }
            .header p { margin: 0; }
            .header h3 { margin-top: 20px; margin-bottom: 10px; }
            .header hr { margin-top: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .summary { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${vendor_store_name}</h2>
            <p>${vendor_address}</p>
            <p>${vendor_phone} | ${vendor_email}</p>
            <h3>Sales Report by Categories</h3>
            <hr>
          </div>
          <div class="summary">
            <p><strong>Date Range:</strong> ${dateRange.start} to ${dateRange.end}</p>
            <p><strong>Total Sales:</strong> ${data.summary?.total_sales || 0}</p>
            <p><strong>Total Quantity:</strong> ${data.summary?.total_quantity || 0}</p>
            <p><strong>Total Revenue:</strong> $${(data.summary?.total_amount || 0).toFixed(2)}</p>
            <p><strong>Total Tax:</strong> $${(data.summary?.total_tax || 0).toFixed(2)}</p>
            <p><strong>Total Discount:</strong> $${(data.summary?.total_discount || 0).toFixed(2)}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Category Name</th>
                <th>Sales</th>
                <th>Quantity Sold</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              ${data.details.map(item => `
                <tr>
                  <td>${item.category_name || 'N/A'}</td>
                  <td>${item.total_sales || 0}</td>
                  <td>${item.total_quantity || 0}</td>
                  <td>$${(item.total_amount || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const columns = [
    {
      name: "Category Name",
      selector: (row) => row.category_name || "N/A",
      sortable: true,
    },
    { name: "Sales", selector: (row) => row.total_sales || 0, sortable: true },
    {
      name: "Quantity Sold",
      selector: (row) => row.total_quantity || 0,
      sortable: true,
    },
    {
      name: "Revenue",
      selector: (row) => `$${(row.total_amount || 0).toFixed(2)}`,
      sortable: true,
    },
  ];

  return (
    <div className="page-body">
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          {backTo && (
            <Button color="primary" onClick={() => dispatch(openView(backTo))}>
              Back
            </Button>
          )}
          <h2 className="text-center flex-grow-1">Sales Report by Category</h2>
          <div></div>
        </div>

        {/* Filters */}
        <div className="d-flex mb-3 gap-2">
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) =>
              setDateRange({ ...dateRange, start: e.target.value })
            }
          />
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) =>
              setDateRange({ ...dateRange, end: e.target.value })
            }
          />
          <Button color="primary" onClick={fetchSalesReport}>
            Filter
          </Button>
          <Button
            color="info"
            onClick={() => {
              setDateRange({
                start: new Date(new Date().setMonth(new Date().getMonth() - 1))
                  .toISOString()
                  .split("T")[0],
                end: new Date().toISOString().split("T")[0],
              });
              fetchSalesReport();
            }}
          >
            Reset
          </Button>
        </div>

        {/* Tabs */}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "summary" })}
              onClick={() => setActiveTab("summary")}
              style={{ cursor: "pointer" }}
            >
              Summary
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "listing" })}
              onClick={() => setActiveTab("listing")}
              style={{ cursor: "pointer" }}
            >
              Listing
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="summary">
            {loading ? (
              <Spinner color="primary" />
            ) : (
              <Row className="mb-4">
                {[
                  {
                    icon: <FaCashRegister size={30} className="text-success mb-2" />,
                    label: "Total Sales",
                    value: data.summary?.total_sales || 0,
                  },
                  {
                    icon: <FaShoppingCart size={30} className="text-warning mb-2" />,
                    label: "Total Quantity",
                    value: data.summary?.total_quantity || 0,
                  },
                  {
                    icon: <FaDollarSign size={30} className="text-success mb-2" />,
                    label: "Total Revenue",
                    value: `$${(data.summary?.total_amount || 0).toFixed(2)}`,
                  },
                  {
                    icon: <FaReceipt size={30} className="text-danger mb-2" />,
                    label: "Total Tax",
                    value: `$${(data.summary?.total_tax || 0).toFixed(2)}`,
                  },
                  {
                    icon: <FaPercent size={30} className="text-info mb-2" />,
                    label: "Total Discount",
                    value: `$${(data.summary?.total_discount || 0).toFixed(2)}`,
                  },
                ].map((item, index) => (
                  <Col md="3" key={index}>
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        {item.icon}
                        <CardTitle tag="h5">{item.label}</CardTitle>
                        <h4>{item.value}</h4>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </TabPane>

          <TabPane tabId="listing">
            {loading ? (
              <Spinner color="primary" />
            ) : (
              <Card className="p-3 shadow-sm">
                <h4 className="mb-3">Sales by Categories</h4>
                <DataTable
                  columns={columns}
                  data={data.details}
                  pagination
                  striped
                />
              </Card>
            )}
          </TabPane>
        </TabContent>
        
        <div className="text-center mt-4 mb-4">
          <Button color="success" onClick={printReport}>
            Print Report
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default SalesReportByCategory;
